import { LeagueParticipant, LeagueStatus } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { EmptyState } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

interface UserResult {
  id: string;
  email: string;
  name?: string;
}

interface ParticipantsTabProps {
  participants: LeagueParticipant[];
  leagueStatus: LeagueStatus;
  isOwner: boolean;
  onAddParticipant: (name: string, userId?: string) => void;
  onRemoveParticipant: (participantId: string) => void;
  onSearchUser: (query: string) => Promise<UserResult[]>;
  onNavigateToProfile: (userId: string) => void;
  colors: any;
}

export const ParticipantsTab = ({
  participants,
  leagueStatus,
  isOwner,
  onAddParticipant,
  onRemoveParticipant,
  onSearchUser,
  onNavigateToProfile,
  colors,
}: ParticipantsTabProps) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [participantName, setParticipantName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canAdd = isOwner;
  const canRemove = isOwner && leagueStatus === 'setup';

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        setSearching(true);
        const results = await onSearchUser(searchQuery.trim());
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery]);

  const handleAddManual = () => {
    if (participantName.trim()) {
      onAddParticipant(participantName.trim());
      setParticipantName('');
      setModalVisible(false);
    }
  };

  const handleAddRegistered = () => {
    if (selectedUser) {
      onAddParticipant(selectedUser.name || selectedUser.email.split('@')[0], selectedUser.id);
      resetSearchState();
      setModalVisible(false);
    }
  };

  const resetSearchState = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedUser(null);
  };

  return (
    <View style={styles.container}>
      {canAdd && (
        <TouchableOpacity
          style={[styles.addButton, { backgroundColor: colors.primary }]}
          onPress={() => setModalVisible(true)}
        >
          <Ionicons name="add-circle-outline" size={20} color={colors.primaryText} />
          <Text style={[styles.addButtonText, { color: colors.primaryText }]}>{t('tournaments.addParticipant')}</Text>
        </TouchableOpacity>
      )}

      <FlatList
        data={participants}
        keyExtractor={(item) => item.id}
        renderItem={({ item, index }) => {
          const navigable = !!item.userId && !canRemove;
          return (
            <TouchableOpacity
              style={[styles.participantRow, { backgroundColor: colors.card, borderColor: colors.border }]}
              onPress={navigable ? () => onNavigateToProfile(item.userId!) : undefined}
              activeOpacity={navigable ? 0.7 : 1}
            >
              <View style={styles.participantInfo}>
                <Text style={[styles.seed, { color: colors.textTertiary }]}>{index + 1}</Text>
                <Text style={[styles.name, { color: colors.text }]}>{item.name}</Text>
                {item.userId && (
                  <Ionicons name="person-circle" size={16} color={colors.primary} />
                )}
              </View>
              {canRemove ? (
                <TouchableOpacity onPress={() => onRemoveParticipant(item.id)}>
                  <Ionicons name="close-circle" size={22} color={colors.error} />
                </TouchableOpacity>
              ) : item.userId ? (
                <Ionicons name="chevron-forward" size={18} color={colors.textTertiary} />
              ) : null}
            </TouchableOpacity>
          );
        }}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <EmptyState
            icon={<Ionicons name="people-outline" size={52} color={colors.textTertiary} />}
            title={t('tournaments.noParticipants')}
            subtitle={canAdd ? t('tournaments.addParticipants') : undefined}
            colors={colors}
          />
        }
      />

      <Modal visible={modalVisible} transparent animationType="slide" onRequestClose={() => setModalVisible(false)}>
        <View style={[styles.modalOverlay, { backgroundColor: colors.overlay }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.surface }]}>
            <Text style={[styles.modalTitle, { color: colors.text }]}>{t('tournaments.addParticipant')}</Text>

            <View style={styles.modeToggle}>
              <TouchableOpacity
                style={[
                  styles.modeButton,
                  !searchMode && { backgroundColor: colors.primary },
                  { borderColor: colors.border },
                ]}
                onPress={() => setSearchMode(false)}
              >
                <Text style={[styles.modeButtonText, { color: !searchMode ? colors.primaryText : colors.text }]}>
                  {t('tournaments.manual')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.modeButton,
                  searchMode && { backgroundColor: colors.primary },
                  { borderColor: colors.border },
                ]}
                onPress={() => setSearchMode(true)}
              >
                <Text style={[styles.modeButtonText, { color: searchMode ? colors.primaryText : colors.text }]}>
                  {t('tournaments.searchByEmail')}
                </Text>
              </TouchableOpacity>
            </View>

            {!searchMode ? (
              <View style={styles.inputSection}>
                <TextInput
                  style={[styles.modalInput, { backgroundColor: colors.card, color: colors.text, borderColor: colors.border }]}
                  placeholder={t('tournaments.participantName')}
                  placeholderTextColor={colors.textTertiary}
                  value={participantName}
                  onChangeText={setParticipantName}
                  autoFocus
                />
                <TouchableOpacity
                  style={[styles.modalButton, { backgroundColor: colors.primary }]}
                  onPress={handleAddManual}
                >
                  <Text style={[styles.modalButtonText, { color: colors.primaryText }]}>{t('common.add')}</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.inputSection}>
                {selectedUser ? (
                  <TouchableOpacity
                    style={[styles.selectedUserCard, { backgroundColor: colors.card, borderColor: colors.primary }]}
                    onPress={() => setSelectedUser(null)}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.resultName, { color: colors.text }]}>
                        {selectedUser.name || selectedUser.email}
                      </Text>
                      {selectedUser.name && (
                        <Text style={[styles.resultEmail, { color: colors.textSecondary }]}>{selectedUser.email}</Text>
                      )}
                    </View>
                    <Text style={{ color: colors.textSecondary, fontSize: 20, paddingLeft: 8 }}>✕</Text>
                  </TouchableOpacity>
                ) : (
                  <>
                    <TextInput
                      style={[styles.modalInput, { backgroundColor: colors.card, color: colors.text, borderColor: colors.border }]}
                      placeholder={t('tournaments.searchNameOrEmailPlaceholder')}
                      placeholderTextColor={colors.textTertiary}
                      value={searchQuery}
                      onChangeText={setSearchQuery}
                      autoCapitalize="none"
                      autoCorrect={false}
                    />

                    {searching && (
                      <ActivityIndicator color={colors.primary} />
                    )}

                    {!searching && searchResults.length > 0 && (
                      <FlatList
                        data={searchResults}
                        keyExtractor={(item) => item.id}
                        style={[styles.resultsList, { borderColor: colors.border }]}
                        keyboardShouldPersistTaps="handled"
                        renderItem={({ item }) => (
                          <TouchableOpacity
                            style={[styles.searchResultCard, { backgroundColor: colors.card, borderColor: colors.border }]}
                            onPress={() => {
                              setSelectedUser(item);
                              setSearchQuery('');
                              setSearchResults([]);
                            }}
                          >
                            <View>
                              <Text style={[styles.resultName, { color: colors.text }]}>
                                {item.name || item.email}
                              </Text>
                              {item.name && (
                                <Text style={[styles.resultEmail, { color: colors.textSecondary }]}>{item.email}</Text>
                              )}
                            </View>
                            <Ionicons name="add-circle" size={24} color={colors.primary} />
                          </TouchableOpacity>
                        )}
                      />
                    )}

                    {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                      <Text style={{ color: colors.textSecondary, fontSize: 14, textAlign: 'center' }}>
                        {t('alerts.userNotFound')}
                      </Text>
                    )}
                  </>
                )}

                <TouchableOpacity
                  style={[styles.modalButton, { backgroundColor: selectedUser ? colors.primary : colors.border }]}
                  onPress={handleAddRegistered}
                  disabled={!selectedUser}
                >
                  <Text style={[styles.modalButtonText, { color: colors.primaryText }]}>{t('common.add')}</Text>
                </TouchableOpacity>
              </View>
            )}

            <TouchableOpacity
              style={[styles.cancelButton, { borderColor: colors.border }]}
              onPress={() => {
                setModalVisible(false);
                setParticipantName('');
                resetSearchState();
              }}
            >
              <Text style={[styles.cancelButtonText, { color: colors.text }]}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 15,
    padding: 12,
    borderRadius: 10,
    gap: 8,
  },
  addButtonText: { fontSize: 15, fontWeight: '600' },
  lockedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: 15,
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  lockedText: { fontSize: 14, flex: 1 },
  listContent: { paddingHorizontal: 15, paddingBottom: 20 },
  participantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    marginBottom: 8,
  },
  participantInfo: { flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 },
  seed: { fontSize: 14, fontWeight: '600', width: 24 },
  name: { fontSize: 16, flex: 1 },
  modalOverlay: { flex: 1, justifyContent: 'center', padding: 20 },
  modalContent: { borderRadius: 16, padding: 20, gap: 16 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', textAlign: 'center' },
  modeToggle: { flexDirection: 'row', gap: 8 },
  modeButton: { flex: 1, padding: 10, borderRadius: 8, alignItems: 'center', borderWidth: 1 },
  modeButtonText: { fontSize: 14, fontWeight: '500' },
  inputSection: { gap: 12 },
  modalInput: { padding: 14, borderRadius: 10, fontSize: 16, borderWidth: 1 },
  resultsList: { borderWidth: 1, borderRadius: 10, maxHeight: 200 },
  selectedUserCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
  },
  searchResultCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 14,
    borderBottomWidth: 1,
  },
  resultName: { fontSize: 16, fontWeight: '500' },
  resultEmail: { fontSize: 13, marginTop: 2 },
  modalButton: { padding: 14, borderRadius: 10, alignItems: 'center' },
  modalButtonText: { fontSize: 16, fontWeight: '600' },
  cancelButton: { padding: 14, borderRadius: 10, alignItems: 'center', borderWidth: 1 },
  cancelButtonText: { fontSize: 16, fontWeight: '500' },
});
